import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import GameIcon from './components/GameIcon';

const DressUpAvatar = ({ onClose, onBack, API, currentUser, userWardrobe = [], userId, gameSettings = {} }) => {
  // Equipped items on mannequin
  const [equippedItems, setEquippedItems] = useState({
    headwear: [],
    top: [],
    outerwear: [],
    bottom: [],
    shoes: [],
    accessories: []
  });
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedEquippedItem, setSelectedEquippedItem] = useState(null);

  // Per-item transforms: { [itemId]: { x, y, scale } }
  // x,y are percentage offsets from the default zone center, scale is multiplier
  const [itemTransforms, setItemTransforms] = useState({});

  // Sidebar
  const [currentCategory, setCurrentCategory] = useState('body');
  const [allItems, setAllItems] = useState([]);
  const [isItemsPanelCollapsed, setIsItemsPanelCollapsed] = useState(false);

  // Color adjustments
  const [showColorPanel, setShowColorPanel] = useState(false);
  const [colorPanelPosition, setColorPanelPosition] = useState({ x: 20, y: 100 });
  const [colorPanelSize, setColorPanelSize] = useState({ width: 320, height: 400 });
  const [isDraggingColorPanel, setIsDraggingColorPanel] = useState(false);
  const [isResizingColorPanel, setIsResizingColorPanel] = useState(false);
  const [colorPanelDragStart, setColorPanelDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [itemAdjustments, setItemAdjustments] = useState({});

  // Notes
  const [notes, setNotes] = useState([]);
  const [noteCounter, setNoteCounter] = useState(0);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);

  // Outfits
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showMyOutfits, setShowMyOutfits] = useState(false);
  const [savedOutfits, setSavedOutfits] = useState([]);

  // Paper doll: avatar parts from admin-managed categories
  const [avatarParts, setAvatarParts] = useState([]);
  const [avatarCategories, setAvatarCategories] = useState([]);
  const [selectedParts, setSelectedParts] = useState({}); // { [categoryKey]: partObj }
  // Full avatar images (from admin Avatar Images)
  const [clothingAvatars, setClothingAvatars] = useState([]);
  const [selectedClothingAvatar, setSelectedClothingAvatar] = useState(null);

  // Zoom step (1%, 5%, 10%, or 25%)
  const [zoomStep, setZoomStep] = useState(0.05);

  // Grid snap
  const [snapToGrid, setSnapToGrid] = useState(false);

  // Canvas background
  const [canvasBackground, setCanvasBackground] = useState('default');

  // Item search
  const [itemSearch, setItemSearch] = useState('');

  // Undo/Redo history (keeps up to 30 snapshots)
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const isUndoRedoRef = useRef(false);

  const saveSnapshot = useCallback(() => {
    if (isUndoRedoRef.current) return;
    const snapshot = {
      equippedItems: JSON.parse(JSON.stringify(equippedItems)),
      itemTransforms: JSON.parse(JSON.stringify(itemTransforms)),
      itemAdjustments: JSON.parse(JSON.stringify(itemAdjustments)),
    };
    // Cut off any future history if we're in the middle
    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push(snapshot);
    // Keep max 30
    if (newHistory.length > 30) newHistory.shift();
    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;
  }, [equippedItems, itemTransforms, itemAdjustments]);

  // Save snapshot on meaningful changes (debounced to avoid saving every pixel of drag)
  const snapshotTimerRef = useRef(null);
  useEffect(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }
    clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => {
      saveSnapshot();
    }, 300);
    return () => clearTimeout(snapshotTimerRef.current);
  }, [equippedItems, itemTransforms, itemAdjustments, saveSnapshot]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    isUndoRedoRef.current = true;
    setEquippedItems(snapshot.equippedItems);
    setItemTransforms(snapshot.itemTransforms);
    setItemAdjustments(snapshot.itemAdjustments);
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    isUndoRedoRef.current = true;
    setEquippedItems(snapshot.equippedItems);
    setItemTransforms(snapshot.itemTransforms);
    setItemAdjustments(snapshot.itemAdjustments);
  }, []);

  // UI
  const [showTips, setShowTips] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const canvasRef = useRef(null);

  // Helper for selectedParts
  const getSelectedPart = (categoryKey) => selectedParts[categoryKey] || null;
  const setSelectedPart = (categoryKey, part) => {
    setSelectedParts(prev => ({ ...prev, [categoryKey]: part }));
  };

  // Categories for tabs — avatar parts from DB first, then clothing
  const CATEGORIES = useMemo(() => {
    // Avatar part categories from admin DB
    const avatarCats = avatarCategories.map(cat => ({
      id: cat.key,
      icon: cat.icon || '🧩',
      name: cat.name,
      darija: '',
      isAvatarPart: true,
      sortOrder: cat.sort_order || 0,
    }));
    // Fallback: if no categories loaded yet, show default body + head
    const defaultAvatarCats = avatarCats.length > 0 ? avatarCats : [
      { id: 'body', icon: gameSettings.icon_body || '🧍', name: 'Body', darija: 'Jism', isAvatarPart: true, sortOrder: 0 },
      { id: 'head', icon: gameSettings.icon_head || '💇', name: 'Head', darija: 'Ras', isAvatarPart: true, sortOrder: 1 },
    ];
    // Clothing categories (always present)
    const clothingCats = [
      { id: 'headwear', icon: gameSettings.icon_headwear || '🎩', name: 'Headwear', darija: 'Tqashir' },
      { id: 'top', icon: gameSettings.icon_tops || '👕', name: 'Tops', darija: 'Lfuq' },
      { id: 'outerwear', icon: gameSettings.icon_outerwear || '🧥', name: 'Outerwear', darija: 'Jakat' },
      { id: 'bottom', icon: gameSettings.icon_bottoms || '👖', name: 'Bottoms', darija: 'Ltaht' },
      { id: 'shoes', icon: gameSettings.icon_shoes || '👟', name: 'Shoes', darija: 'Sabbat' },
      { id: 'accessories', icon: gameSettings.icon_accessories || '👜', name: 'Accessories', darija: 'Zwaqa' },
    ];
    // Full avatar tab (only if avatars exist in admin)
    const fullAvatarTab = clothingAvatars.length > 0 ? [
      { id: '_avatar', icon: '👤', name: 'Avatar', darija: '', isFullAvatar: true, sortOrder: -1 }
    ] : [];
    return [...fullAvatarTab, ...defaultAvatarCats, ...clothingCats];
  }, [avatarCategories, gameSettings, clothingAvatars]);

  // Drop zone positions (percentage of mannequin container)
  const DROP_ZONES = useMemo(() => {
    const zones = {
      // Full avatar — behind everything
      _avatar:    { top: '0%',  left: '10%', width: '80%', height: '100%', zIndex: 2 },
      // Clothing zones
      headwear:   { top: '0%',  left: '25%', width: '50%', height: '12%', zIndex: 50 },
      top:        { top: '18%', left: '15%', width: '70%', height: '24%', zIndex: 30 },
      outerwear:  { top: '16%', left: '10%', width: '80%', height: '28%', zIndex: 40 },
      bottom:     { top: '42%', left: '18%', width: '64%', height: '28%', zIndex: 20 },
      shoes:      { top: '82%', left: '18%', width: '64%', height: '12%', zIndex: 10 },
      accessories:{ top: '8%',  left: '65%', width: '30%', height: '14%', zIndex: 60 },
    };
    // Add zones for each avatar part category (body, head, etc.)
    CATEGORIES.filter(c => c.isAvatarPart).forEach((cat, idx) => {
      zones[`_part_${cat.id}`] = { top: '0%', left: '10%', width: '80%', height: '100%', zIndex: idx === 0 ? 1 : 100 + idx };
    });
    return zones;
  }, [CATEGORIES]);

  // Map layer_type from DB to our zone categories
  const mapLayerToCategory = (layerType) => {
    const map = {
      'headwear': 'headwear',
      'top': 'top',
      'base': 'top',
      'outerwear': 'outerwear',
      'bottom': 'bottom',
      'shoes': 'shoes',
      'accessories': 'accessories',
    };
    return map[layerType] || 'accessories';
  };

  // Fetch owned items
  useEffect(() => {
    const fetchOwnedItems = async () => {
      if (!userId) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('user_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/souk-clothes/owned-items/${userId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          let items = data.items || [];

          // If user has 0 items, claim free starter items
          if (items.length === 0) {
            try {
              const token = localStorage.getItem('user_token');
              const claimRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/souk-clothes/claim-starter-items/${userId}`, {
                method: 'POST',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              if (claimRes.ok) {
                const claimData = await claimRes.json();
                items = claimData.items || [];
              }
            } catch (err) {
              console.error('Error claiming starter items:', err);
            }
          }

          setAllItems(items);
        }
      } catch (error) {
        console.error('Error fetching owned items:', error);
      }
      setLoading(false);
    };
    fetchOwnedItems();
  }, [userId]);

  // Load saved outfits
  useEffect(() => { loadSavedOutfits(); }, []);

  // Fetch avatar parts and categories from admin
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    // Fetch parts
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/souk-clothes/avatar-parts`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setAvatarParts((Array.isArray(data) ? data : []).filter(p => p.image_url)))
      .catch(err => console.error('Error fetching avatar parts:', err));
    // Fetch categories
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/souk-clothes/avatar-categories`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setAvatarCategories(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching avatar categories:', err));
    // Fetch full avatar images
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/souk-clothes/avatars`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setClothingAvatars(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching avatars:', err));
  }, []);

  // Handle color panel resize
  useEffect(() => {
    if (!isResizingColorPanel) return;
    const handleResize = (e) => {
      const clientX = e.clientX || e.touches?.[0]?.clientX;
      const clientY = e.clientY || e.touches?.[0]?.clientY;
      const isMobile = window.innerWidth < 768;
      const minW = isMobile ? 280 : 250;
      const maxW = isMobile ? window.innerWidth * 0.95 : 500;
      setColorPanelSize({
        width: Math.max(minW, Math.min(maxW, resizeStart.width + (clientX - resizeStart.x))),
        height: Math.max(280, Math.min(window.innerHeight * 0.7, resizeStart.height + (clientY - resizeStart.y)))
      });
    };
    const stop = () => setIsResizingColorPanel(false);
    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', handleResize, { passive: false });
    window.addEventListener('touchend', stop);
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', handleResize);
      window.removeEventListener('touchend', stop);
    };
  }, [isResizingColorPanel, resizeStart]);

  // Hide bottom nav and prevent scroll
  useEffect(() => {
    document.body.classList.add('hide-bottom-nav');
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    return () => {
      document.body.classList.remove('hide-bottom-nav');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape — deselect or close modals
      if (e.key === 'Escape') {
        if (showColorPanel) { setShowColorPanel(false); return; }
        if (selectedEquippedItem) { setSelectedEquippedItem(null); return; }
        if (selectedNote) { setSelectedNote(null); return; }
      }
      // Undo/Redo — works always (even without selection)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      // Don't handle shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!selectedEquippedItem) return;
      const { zone, itemId } = selectedEquippedItem;
      const t = getTransform(itemId);
      const nudge = e.shiftKey ? 1 : 5;
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          unequipItem(zone, itemId);
          setSelectedEquippedItem(null);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          updateTransform(itemId, { x: (t.x || 0) - nudge });
          break;
        case 'ArrowRight':
          e.preventDefault();
          updateTransform(itemId, { x: (t.x || 0) + nudge });
          break;
        case 'ArrowUp':
          e.preventDefault();
          updateTransform(itemId, { y: (t.y || 0) - nudge });
          break;
        case 'ArrowDown':
          e.preventDefault();
          updateTransform(itemId, { y: (t.y || 0) + nudge });
          break;
        case '[':
          e.preventDefault();
          updateTransform(itemId, { zIndex: Math.max(1, (t.zIndex || 1) - 1) });
          break;
        case ']':
          e.preventDefault();
          updateTransform(itemId, { zIndex: (t.zIndex || 1) + 1 });
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEquippedItem, selectedNote, showColorPanel]);

  const loadSavedOutfits = async () => {
    try {
      const token = localStorage.getItem('user_token');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dress-up/outfits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedOutfits(data.outfits || []);
      }
    } catch (error) {
      console.error('Error loading outfits:', error);
    }
  };

  // Group items by category
  const itemsByCategory = allItems.reduce((acc, item) => {
    const cat = mapLayerToCategory(item.layer_type);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Check if item is currently equipped
  const isItemEquipped = (itemId) => {
    return Object.values(equippedItems).some(
      arr => Array.isArray(arr) && arr.some(item => item.id === itemId)
    );
  };

  // Equip item to correct zone (all zones are arrays — allows multiple items per category)
  const equipItem = (item) => {
    const zone = mapLayerToCategory(item.layer_type);
    const alreadyEquipped = (equippedItems[zone] || []).some(a => a.id === item.id);

    setEquippedItems(prev => {
      const next = { ...prev };
      const existing = prev[zone] || [];
      if (existing.some(a => a.id === item.id)) {
        next[zone] = existing.filter(a => a.id !== item.id);
      } else {
        next[zone] = [...existing, item];
      }
      return next;
    });

    // Auto-select when equipping, deselect when unequipping
    if (!alreadyEquipped) {
      // Offset new items so they don't stack exactly on top of existing ones
      const existingCount = (equippedItems[zone] || []).length;
      if (existingCount > 0) {
        updateTransform(item.id, { x: existingCount * 20, y: existingCount * 15 });
      }
      setSelectedEquippedItem({ zone, itemId: item.id });
    } else {
      setSelectedEquippedItem(null);
    }
  };

  // Unequip item
  const unequipItem = (zone, itemId) => {
    setEquippedItems(prev => {
      const next = { ...prev };
      next[zone] = (prev[zone] || []).filter(a => a.id !== itemId);
      return next;
    });
    setSelectedEquippedItem(null);
  };

  // Get/set per-item transform (position offset + scale)
  const getTransform = (itemId) => {
    return itemTransforms[itemId] || { x: 0, y: 0, scale: 1, zIndex: 0, flipX: false, rotation: 0 };
  };

  const updateTransform = (itemId, updates) => {
    setItemTransforms(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { x: 0, y: 0, scale: 1, zIndex: 0, flipX: false, rotation: 0 }), ...updates }
    }));
  };

  // Get color adjustments for an item
  const getAdjustments = (itemId) => {
    return itemAdjustments[itemId] || { hue: 0, brightness: 100, contrast: 100, saturation: 100, opacity: 100 };
  };

  const updateAdjustment = (itemId, key, value) => {
    setItemAdjustments(prev => ({
      ...prev,
      [itemId]: { ...getAdjustments(itemId), [key]: value }
    }));
  };

  const resetAdjustments = (itemId) => {
    setItemAdjustments(prev => ({
      ...prev,
      [itemId]: { hue: 0, brightness: 100, contrast: 100, saturation: 100, opacity: 100 }
    }));
  };

  // Notes
  const addNote = (text) => {
    setNotes(prev => [...prev, {
      id: `note-${noteCounter}`,
      text,
      x: 20 + Math.random() * 150,
      y: 10 + Math.random() * 40,
      zIndex: 1000 + noteCounter
    }]);
    setNoteCounter(c => c + 1);
  };

  const deleteNote = (noteId) => setNotes(prev => prev.filter(n => n.id !== noteId));
  const updateNote = (noteId, updates) => setNotes(prev => prev.map(n => n.id === noteId ? { ...n, ...updates } : n));

  // Save outfit
  const showError = (msg) => {
    setErrorMessage(msg);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 3000);
  };

  const saveOutfit = async (outfitName, occasion) => {
    try {
      const token = localStorage.getItem('user_token');
      if (!token) { showError('Please log in again to save outfits.'); return; }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dress-up/outfits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: outfitName,
          occasion,
          items: equippedItems,
          equippedItems,
          selectedParts,
          adjustments: itemAdjustments,
          transforms: itemTransforms,
          notes
        })
      });
      if (response.ok) {
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
        loadSavedOutfits();
        setShowSaveModal(false);
      } else {
        showError('Failed to save outfit. Please try again.');
      }
    } catch (error) {
      console.error('Error saving outfit:', error);
      showError('Error saving outfit. Please try again.');
    }
  };

  const deleteOutfit = async (outfitId) => {
    try {
      const token = localStorage.getItem('user_token');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dress-up/outfits/${outfitId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) loadSavedOutfits();
    } catch (error) {
      console.error('Error deleting outfit:', error);
    }
  };

  const loadOutfit = (outfit) => {
    if (outfit.equippedItems) {
      // Backward compat: migrate old single-item format to arrays
      const migrated = {};
      for (const [zone, val] of Object.entries(outfit.equippedItems)) {
        if (Array.isArray(val)) {
          migrated[zone] = val;
        } else if (val) {
          migrated[zone] = [val];
        } else {
          migrated[zone] = [];
        }
      }
      // Migrate old selectedParts format into equippedItems zones
      if (outfit.selectedParts) {
        for (const [catKey, part] of Object.entries(outfit.selectedParts)) {
          if (part && part.image_url) {
            const zone = `_part_${catKey}`;
            if (!migrated[zone] || migrated[zone].length === 0) {
              migrated[zone] = [{ id: part.id, image_url: part.image_url, name_english: part.name, icon: '🧩' }];
            }
          }
        }
      }
      setEquippedItems(migrated);
    }
    if (outfit.adjustments) {
      setItemAdjustments(outfit.adjustments);
    }
    if (outfit.transforms) {
      setItemTransforms(outfit.transforms);
    }
    setNotes(outfit.notes || []);
    setNoteCounter(outfit.notes?.length || 0);
    setShowMyOutfits(false);
  };

  // Clear all
  const clearAll = () => {
    setEquippedItems({ headwear: [], top: [], outerwear: [], bottom: [], shoes: [], accessories: [] });
    setItemAdjustments({});
    setItemTransforms({});
    setNotes([]);
    setSelectedEquippedItem(null);
  };

  // Duplicate selected item on canvas
  const duplicateItem = () => {
    if (!selectedEquippedItem) return;
    const { zone, itemId } = selectedEquippedItem;
    const item = (equippedItems[zone] || []).find(a => a.id === itemId);
    if (!item) return;
    const copyId = `${itemId}_copy_${Date.now()}`;
    // Count existing copies of this item to number them
    const existingCount = (equippedItems[zone] || []).filter(a =>
      a.id === itemId || a.id.startsWith(`${itemId}_copy_`) ||
      (a._sourceId && a._sourceId === (item._sourceId || itemId))
    ).length;
    const copyName = `${item.name_english || ''}${existingCount > 0 ? ` ${existingCount + 1}` : ' 2'}`;
    const copy = { ...item, id: copyId, name_english: copyName, _sourceId: item._sourceId || itemId };
    setEquippedItems(prev => ({
      ...prev,
      [zone]: [...(prev[zone] || []), copy]
    }));
    const oldTransform = getTransform(itemId);
    updateTransform(copyId, { ...oldTransform, x: (oldTransform.x || 0) + 30, y: (oldTransform.y || 0) + 30 });
    setSelectedEquippedItem({ zone, itemId: copyId });
  };

  // Canvas background styles
  const canvasBgStyles = {
    default: 'bg-gradient-to-b from-gray-100 to-gray-200',
    white: 'bg-white',
    dark: 'bg-gray-800',
    checkered: '',
    beige: 'bg-amber-50',
  };
  const canvasBgInline = canvasBackground === 'checkered' ? {
    backgroundImage: 'linear-gradient(45deg, #e5e5e5 25%, transparent 25%), linear-gradient(-45deg, #e5e5e5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e5 75%), linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)',
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
    backgroundColor: '#ffffff'
  } : {};

  // Check if any item is equipped
  const hasEquippedItems = Object.values(equippedItems).some(
    arr => Array.isArray(arr) && arr.length > 0
  );

  // Get currently selected equipped item for color panel
  const getSelectedItemObj = () => {
    if (!selectedEquippedItem) return null;
    const { zone, itemId } = selectedEquippedItem;
    return (equippedItems[zone] || []).find(a => a.id === itemId) || null;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center z-[60]">
        <div className="text-white text-xl font-bold animate-pulse">Loading Dress Up...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center z-[60] overflow-hidden" style={{touchAction: 'none'}}>
      <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 shadow-2xl w-full h-full overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-black/30 backdrop-blur-sm text-white p-2 sm:p-3 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold">👗 Dress Up Avatar</h2>
          <div className="flex gap-1 sm:gap-2">
            <button onClick={() => setShowTips(true)} className="bg-blue-500 hover:bg-blue-600 px-2 py-1 rounded-lg text-xs sm:text-sm font-bold">?</button>
            <button onClick={onClose} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg font-bold text-sm">X</button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

          {/* Left: Category Tabs */}
          <div className="md:w-16 bg-black/30 p-1 md:p-2 overflow-x-auto md:overflow-y-auto flex-shrink-0">
            <div className="flex md:flex-col gap-1">
              {CATEGORIES.map(cat => {
                // Avatar part tabs count from avatarParts, clothing tabs from itemsByCategory
                const count = cat.isFullAvatar
                  ? clothingAvatars.length
                  : cat.isAvatarPart
                    ? avatarParts.filter(p => (p.type || '').split(',').includes(cat.id)).length
                    : (itemsByCategory[cat.id] || []).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setCurrentCategory(cat.id)}
                    className={`flex-shrink-0 p-2 rounded-lg transition-all text-center ${
                      currentCategory === cat.id
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    title={`${cat.name} (${count})`}
                  >
                    <div className="text-lg sm:text-xl"><GameIcon icon={cat.icon} /></div>
                    <div className="text-[8px] sm:text-[10px] leading-tight">{count}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Center: Mannequin Canvas */}
          <div className="flex-1 flex flex-col p-2 md:p-3 min-w-0">
            {/* Top Bar */}
            <div className="bg-black/30 backdrop-blur-sm rounded-lg p-2 mb-2 flex justify-between items-center flex-wrap gap-1">
              <div className="text-white font-bold text-sm truncate max-w-[140px] sm:max-w-[200px]">
                {selectedEquippedItem ? (() => {
                  const selItem = (equippedItems[selectedEquippedItem.zone] || []).find(a => a.id === selectedEquippedItem.itemId);
                  const t = getTransform(selectedEquippedItem.itemId);
                  return selItem ? `${selItem.name_english} ${t.scale !== 1 ? `(${Math.round(t.scale * 100)}%)` : ''}` : 'Tap items to dress up';
                })() : hasEquippedItems ? `${Object.values(equippedItems).flat().filter(Boolean).length} items on` : 'Tap items to dress up'}
              </div>
              <div className="flex gap-1 flex-wrap">
                {/* Size controls for selected item */}
                {selectedEquippedItem && (() => {
                  const t = getTransform(selectedEquippedItem.itemId);
                  return (
                    <>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { scale: Math.max(0.05, t.scale - zoomStep) })}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Smaller"
                      >
                        ➖
                      </button>
                      <span className="text-white text-xs font-bold px-1">{Math.round(t.scale * 100)}%</span>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { scale: Math.min(6, t.scale + zoomStep) })}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Bigger"
                      >
                        ➕
                      </button>
                      <select
                        value={zoomStep}
                        onChange={(e) => setZoomStep(parseFloat(e.target.value))}
                        className="bg-purple-600 text-white text-xs font-bold px-1 py-1 rounded-lg border-none cursor-pointer"
                        title="Zoom step size"
                      >
                        <option value={0.01}>1%</option>
                        <option value={0.05}>5%</option>
                        <option value={0.10}>10%</option>
                        <option value={0.25}>25%</option>
                      </select>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { flipX: !t.flipX })}
                        className={`${t.flipX ? 'bg-pink-500 hover:bg-pink-600' : 'bg-teal-500 hover:bg-teal-600'} text-white px-2 py-1 rounded-lg font-bold text-xs`}
                        title="Flip horizontal"
                      >
                        ↔
                      </button>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { rotation: (t.rotation || 0) - 15 })}
                        className="bg-teal-500 hover:bg-teal-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Rotate left 15°"
                      >
                        ↶
                      </button>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { rotation: (t.rotation || 0) + 15 })}
                        className="bg-teal-500 hover:bg-teal-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Rotate right 15°"
                      >
                        ↷
                      </button>
                      <button
                        onClick={duplicateItem}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Duplicate item on canvas"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => updateTransform(selectedEquippedItem.itemId, { x: 0, y: 0, scale: 1, zIndex: 0, flipX: false, rotation: 0 })}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Reset position & size"
                      >
                        ↺
                      </button>
                      {(() => {
                        // Collect all z-indexes from all equipped items
                        const allZ = Object.entries(equippedItems).flatMap(([z, items]) =>
                          (items || []).map(it => {
                            const tr = getTransform(it.id);
                            return tr.zIndex || DROP_ZONES[z]?.zIndex || 30;
                          })
                        );
                        const maxZ = Math.max(...allZ, 1);
                        const minZ = Math.min(...allZ, 1);
                        return (
                          <>
                            <button
                              onClick={() => updateTransform(selectedEquippedItem.itemId, { zIndex: Math.max(1, minZ - 1) })}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                              title="Send to back"
                            >
                              ⬇
                            </button>
                            <button
                              onClick={() => updateTransform(selectedEquippedItem.itemId, { zIndex: maxZ + 1 })}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                              title="Bring to front"
                            >
                              ⬆
                            </button>
                          </>
                        );
                      })()}
                      <button
                        onClick={() => {
                          unequipItem(selectedEquippedItem.zone, selectedEquippedItem.itemId);
                          setSelectedEquippedItem(null);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-lg font-bold text-xs"
                        title="Remove item"
                      >
                        ❌
                      </button>
                    </>
                  );
                })()}
                <button
                  onClick={() => selectedEquippedItem && setShowColorPanel(!showColorPanel)}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${
                    selectedEquippedItem ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={!selectedEquippedItem}
                >
                  🎨
                </button>
                <button
                  onClick={() => {
                    setEquippedItems(prev => {
                      const next = { ...prev, _avatar: [] };
                      Object.keys(next).filter(k => k.startsWith('_part_')).forEach(k => { next[k] = []; });
                      return next;
                    });
                    setSelectedEquippedItem(null);
                  }}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${
                    !(equippedItems._avatar || []).length && !Object.entries(equippedItems).some(([k, v]) => k.startsWith('_part_') && v?.length > 0)
                      ? 'bg-gray-500 text-gray-300 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  }`}
                  disabled={!(equippedItems._avatar || []).length && !Object.entries(equippedItems).some(([k, v]) => k.startsWith('_part_') && v?.length > 0)}
                  title="Clear avatar (blank canvas)"
                >
                  🚫
                </button>
                <button onClick={() => setShowNoteModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-lg font-bold text-xs">
                  📝+
                </button>
                <button
                  onClick={undo}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${historyIndexRef.current > 0 ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
                  title="Undo (Ctrl+Z)"
                >
                  ↩
                </button>
                <button
                  onClick={redo}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${historyIndexRef.current < historyRef.current.length - 1 ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
                  title="Redo (Ctrl+Y)"
                >
                  ↪
                </button>
                <button onClick={clearAll} className="bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded-lg font-bold text-xs">
                  🔄
                </button>
                <button onClick={() => setShowMyOutfits(true)} className="bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded-lg font-bold text-xs">
                  📂
                </button>
                <button
                  onClick={() => hasEquippedItems && setShowSaveModal(true)}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${
                    hasEquippedItems ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={!hasEquippedItems}
                >
                  💾
                </button>
                <button
                  onClick={() => setSnapToGrid(!snapToGrid)}
                  className={`px-2 py-1 rounded-lg font-bold text-xs ${
                    snapToGrid ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
                  }`}
                  title={snapToGrid ? 'Grid snap ON' : 'Grid snap OFF'}
                >
                  ⊞
                </button>
                <select
                  value={canvasBackground}
                  onChange={(e) => setCanvasBackground(e.target.value)}
                  className="bg-white/20 text-white text-xs font-bold px-1 py-1 rounded-lg border-none cursor-pointer"
                  title="Canvas background"
                >
                  <option value="default" className="text-black">🎨 Default</option>
                  <option value="white" className="text-black">⬜ White</option>
                  <option value="dark" className="text-black">⬛ Dark</option>
                  <option value="checkered" className="text-black">🏁 Check</option>
                  <option value="beige" className="text-black">🟫 Beige</option>
                </select>
              </div>
            </div>

            {/* Item selector chips — tap to select without clicking on canvas */}
            {hasEquippedItems && (
              <div className="flex gap-1 mb-1 overflow-x-auto pb-1 scrollbar-thin">
                {Object.entries(equippedItems).flatMap(([zone, items]) =>
                  (items || []).map(item => {
                    const isSel = selectedEquippedItem?.itemId === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          if (isSel) {
                            setSelectedEquippedItem(null);
                          } else {
                            setSelectedEquippedItem({ zone, itemId: item.id });
                            setSelectedNote(null);
                          }
                        }}
                        className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold truncate max-w-[100px] ${
                          isSel
                            ? 'bg-green-500 text-white ring-2 ring-green-300'
                            : 'bg-white/20 text-white/80 hover:bg-white/30'
                        }`}
                      >
                        {item.name_english || '👕'}
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {/* Canvas with Mannequin */}
            <div
              ref={canvasRef}
              className={`flex-1 ${canvasBgStyles[canvasBackground] || canvasBgStyles.default} rounded-lg relative overflow-hidden shadow-inner`}
              style={canvasBgInline}
              onClick={() => { setSelectedEquippedItem(null); setSelectedNote(null); }}
            >
              {/* Grid overlay */}
              {snapToGrid && (
                <div className="absolute inset-0 pointer-events-none z-[1]" style={{
                  backgroundImage: 'linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)',
                  backgroundSize: '10px 10px'
                }} />
              )}
              {/* Equipped items (avatar, avatar parts, clothing) - each is independently draggable & resizable */}
              {Object.entries(DROP_ZONES).map(([zone, pos]) => {
                const items = equippedItems[zone] || [];
                return items.map(item => (
                  <DraggableClothingItem
                    key={item.id}
                    item={item}
                    zone={zone}
                    zonePos={pos}
                    canvasRef={canvasRef}
                    transform={getTransform(item.id)}
                    adjustments={getAdjustments(item.id)}
                    isSelected={selectedEquippedItem?.itemId === item.id}
                    hasSelection={!!selectedEquippedItem}
                    onSelect={() => {
                      setSelectedEquippedItem({ zone, itemId: item.id });
                      setSelectedNote(null);
                    }}
                    onTransformChange={(updates) => updateTransform(item.id, updates)}
                    zoomStep={zoomStep}
                    snapToGrid={snapToGrid}
                  />
                ));
              })}

              {/* Draggable Notes */}
              {notes.map(note => (
                <DraggableNote
                  key={note.id}
                  note={note}
                  isSelected={selectedNote === note.id}
                  onSelect={() => { setSelectedNote(note.id); setSelectedEquippedItem(null); }}
                  onDelete={() => deleteNote(note.id)}
                  onUpdate={(updates) => updateNote(note.id, updates)}
                />
              ))}
            </div>
          </div>

          {/* Right: Items Panel */}
          <div className={`md:w-56 lg:w-64 bg-black/30 backdrop-blur-sm p-2 overflow-y-auto transition-all ${
            isItemsPanelCollapsed ? 'max-h-10 md:max-h-none' : 'max-h-44 md:max-h-none'
          }`}>
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-white font-bold text-xs sm:text-sm">
                <GameIcon icon={CATEGORIES.find(c => c.id === currentCategory)?.icon} /> {CATEGORIES.find(c => c.id === currentCategory)?.name}
              </h3>
              <button
                onClick={() => setIsItemsPanelCollapsed(!isItemsPanelCollapsed)}
                className="md:hidden text-white bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs"
              >
                {isItemsPanelCollapsed ? '▲' : '▼'}
              </button>
            </div>
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search items..."
              className="w-full bg-white/10 text-white placeholder-white/40 text-xs px-2 py-1 rounded-lg mb-2 outline-none focus:bg-white/20"
            />
            <div className={`grid grid-cols-4 sm:grid-cols-5 md:grid-cols-3 gap-1.5 ${isItemsPanelCollapsed ? 'hidden md:grid' : ''}`}>
              {/* Full Avatar selection */}
              {CATEGORIES.find(c => c.id === currentCategory)?.isFullAvatar && (
                <>
                  {/* Blank option */}
                  <button
                    onClick={() => { setEquippedItems(prev => ({ ...prev, _avatar: [] })); setSelectedEquippedItem(null); }}
                    className={`aspect-square rounded-lg p-1.5 flex flex-col items-center justify-center transition-all hover:scale-105 ${
                      !(equippedItems._avatar || []).length ? 'bg-green-500/40 border-2 border-green-400 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    <div className="text-2xl mb-0.5">🚫</div>
                    <div className="text-[9px] text-center leading-tight">Blank</div>
                  </button>
                  {clothingAvatars.map(avatar => {
                    const isOn = (equippedItems._avatar || []).some(a => a.id === avatar.id);
                    return (
                      <button
                        key={avatar.id}
                        onClick={() => {
                          if (isOn) {
                            setEquippedItems(prev => ({ ...prev, _avatar: [] }));
                            setSelectedEquippedItem(null);
                          } else {
                            const avatarItem = { id: avatar.id, image_url: avatar.image_url, name_english: avatar.name, icon: '👤' };
                            setEquippedItems(prev => ({ ...prev, _avatar: [avatarItem] }));
                            setSelectedEquippedItem({ zone: '_avatar', itemId: avatar.id });
                          }
                        }}
                        className={`aspect-square rounded-lg p-1.5 flex flex-col items-center justify-center transition-all hover:scale-105 ${
                          isOn ? 'bg-green-500/40 border-2 border-green-400 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                      >
                        {avatar.image_url ? (
                          <img src={avatar.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${avatar.image_url}` : avatar.image_url} alt={avatar.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain mb-0.5" />
                        ) : (
                          <div className="text-2xl mb-0.5">👤</div>
                        )}
                        <div className="text-[9px] text-center line-clamp-1 leading-tight">{avatar.name}</div>
                        {isOn && <div className="text-[8px] text-green-300 font-bold">ON</div>}
                      </button>
                    );
                  })}
                </>
              )}
              {/* Avatar part categories (dynamic from admin) */}
              {CATEGORIES.find(c => c.id === currentCategory)?.isAvatarPart && (() => {
                const partZone = `_part_${currentCategory}`;
                const equippedPart = (equippedItems[partZone] || [])[0] || null;
                return (
                  <>
                    {/* None option */}
                    <button
                      onClick={() => { setEquippedItems(prev => ({ ...prev, [partZone]: [] })); setSelectedEquippedItem(null); }}
                      className={`aspect-square rounded-lg p-1.5 flex flex-col items-center justify-center transition-all hover:scale-105 ${
                        !equippedPart ? 'bg-green-500/40 border-2 border-green-400 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      <div className="text-2xl mb-0.5">🚫</div>
                      <div className="text-[9px] text-center leading-tight">None</div>
                    </button>
                    {/* Group parts by gender */}
                    {(() => {
                      const catParts = avatarParts.filter(p => (p.type || '').split(',').includes(currentCategory));
                      if (catParts.length === 0) return (
                        <div className="col-span-full text-white/40 text-center py-4 text-xs">
                          No {CATEGORIES.find(c => c.id === currentCategory)?.name?.toLowerCase()} options uploaded yet
                        </div>
                      );
                      const genderOrder = [
                        { key: 'female', label: '♀ Female', color: 'text-pink-300 border-pink-400/30' },
                        { key: 'male', label: '♂ Male', color: 'text-blue-300 border-blue-400/30' },
                        { key: 'neutral', label: '⚪ Neutral', color: 'text-gray-300 border-gray-400/30' }
                      ];
                      return genderOrder.map(g => {
                        const gParts = catParts.filter(p => (p.gender || 'neutral') === g.key);
                        if (gParts.length === 0) return null;
                        return (
                          <React.Fragment key={g.key}>
                            <div className={`col-span-full ${g.color} text-[10px] font-bold mt-1 border-t pt-1`}>
                              {g.label}
                            </div>
                            {gParts.map(part => {
                              const isOn = equippedPart?.id === part.id;
                              return (
                                <button
                                  key={part.id}
                                  onClick={() => {
                                    if (isOn) {
                                      setEquippedItems(prev => ({ ...prev, [partZone]: [] }));
                                      setSelectedEquippedItem(null);
                                    } else {
                                      const partItem = { id: part.id, image_url: part.image_url, name_english: part.name, icon: '🧩' };
                                      setEquippedItems(prev => ({ ...prev, [partZone]: [partItem] }));
                                      setSelectedEquippedItem({ zone: partZone, itemId: part.id });
                                    }
                                  }}
                                  className={`aspect-square rounded-md p-1 flex flex-col items-center justify-center transition-all hover:scale-105 ${
                                    isOn ? 'bg-green-500/40 border-2 border-green-400 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
                                  }`}
                                >
                                  {part.image_url ? (
                                    <img src={part.image_url} alt={part.name} className="w-6 h-6 sm:w-8 sm:h-8 object-contain" />
                                  ) : (
                                    <div className="text-lg"><GameIcon icon={CATEGORIES.find(c => c.id === currentCategory)?.icon || '🧩'} /></div>
                                  )}
                                  <div className="text-[8px] text-center line-clamp-1 leading-tight">{part.name}</div>
                                  {isOn && <div className="text-[7px] text-green-300 font-bold">ON</div>}
                                </button>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </>
                );
              })()}
              {/* Clothing items tabs (non-avatar categories) */}
              {!CATEGORIES.find(c => c.id === currentCategory)?.isAvatarPart && !CATEGORIES.find(c => c.id === currentCategory)?.isFullAvatar && (
                <>
                  {(itemsByCategory[currentCategory] || []).filter(item => {
                    if (!itemSearch.trim()) return true;
                    const q = itemSearch.toLowerCase();
                    return (item.name_english || '').toLowerCase().includes(q) || (item.name_darija || '').toLowerCase().includes(q);
                  }).map(item => {
                    const equipped = isItemEquipped(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => equipItem(item)}
                        className={`aspect-square rounded-lg p-1.5 flex flex-col items-center justify-center transition-all hover:scale-105 ${
                          equipped
                            ? 'bg-green-500/40 border-2 border-green-400 text-white'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name_english} className="w-8 h-8 sm:w-10 sm:h-10 object-contain mb-0.5" />
                        ) : (
                          <div className="text-2xl sm:text-3xl mb-0.5"><GameIcon icon={item.icon || '👕'} /></div>
                        )}
                        <div className="text-[9px] sm:text-[10px] text-center line-clamp-1 leading-tight">{item.name_english}</div>
                        <div className="text-[8px] sm:text-[9px] text-yellow-400 line-clamp-1 leading-tight">{item.name_darija}</div>
                        {equipped && <div className="text-[8px] text-green-300 font-bold">ON</div>}
                      </button>
                    );
                  })}
                  {(itemsByCategory[currentCategory] || []).length === 0 && (
                    <div className="col-span-full text-white/40 text-center py-6 text-xs">
                      No {CATEGORIES.find(c => c.id === currentCategory)?.name?.toLowerCase()} owned yet
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Color Panel */}
      {showColorPanel && selectedEquippedItem && (() => {
        const selItem = getSelectedItemObj();
        if (!selItem) return null;
        const adj = getAdjustments(selItem.id);
        const isMobile = window.innerWidth < 768;

        return (
          <div
            className="fixed bg-black/95 border-2 border-purple-500 rounded-xl p-3 z-40 shadow-2xl"
            style={{
              left: `${colorPanelPosition.x}px`,
              top: `${colorPanelPosition.y}px`,
              width: `${colorPanelSize.width}px`,
              height: `${colorPanelSize.height}px`,
              minWidth: isMobile ? '280px' : '250px',
              minHeight: '280px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseDown={(e) => {
              if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('.resize-handle')) return;
              setIsDraggingColorPanel(true);
              setColorPanelDragStart({ x: e.clientX - colorPanelPosition.x, y: e.clientY - colorPanelPosition.y });
            }}
            onTouchStart={(e) => {
              if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('.resize-handle')) return;
              const t = e.touches[0];
              setIsDraggingColorPanel(true);
              setColorPanelDragStart({ x: t.clientX - colorPanelPosition.x, y: t.clientY - colorPanelPosition.y });
            }}
            onMouseMove={(e) => {
              if (isDraggingColorPanel) {
                setColorPanelPosition({
                  x: Math.max(0, Math.min(window.innerWidth - colorPanelSize.width, e.clientX - colorPanelDragStart.x)),
                  y: Math.max(0, Math.min(window.innerHeight - colorPanelSize.height, e.clientY - colorPanelDragStart.y))
                });
              }
            }}
            onTouchMove={(e) => {
              if (isDraggingColorPanel) {
                const t = e.touches[0];
                setColorPanelPosition({
                  x: Math.max(0, Math.min(window.innerWidth - colorPanelSize.width, t.clientX - colorPanelDragStart.x)),
                  y: Math.max(0, Math.min(window.innerHeight - colorPanelSize.height, t.clientY - colorPanelDragStart.y))
                });
              }
            }}
            onMouseUp={() => setIsDraggingColorPanel(false)}
            onTouchEnd={() => setIsDraggingColorPanel(false)}
            onMouseLeave={() => setIsDraggingColorPanel(false)}
          >
            {/* Resize handle */}
            <div
              className="resize-handle absolute bottom-0 right-0 w-8 h-8 cursor-nwse-resize z-50 touch-none"
              style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(147,51,234,0.9) 50%)', borderBottomRightRadius: '0.75rem' }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setIsResizingColorPanel(true);
                setResizeStart({ x: e.clientX, y: e.clientY, width: colorPanelSize.width, height: colorPanelSize.height });
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const t = e.touches[0];
                setIsResizingColorPanel(true);
                setResizeStart({ x: t.clientX, y: t.clientY, width: colorPanelSize.width, height: colorPanelSize.height });
              }}
            />

            <div className="flex justify-between items-center mb-2 cursor-move">
              <h4 className="text-white font-bold text-sm">🎨 {selItem.name_english}</h4>
              <button onClick={() => resetAdjustments(selItem.id)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded">Reset</button>
            </div>

            <div className="space-y-3 overflow-y-auto flex-1 pr-1">
              {[
                { key: 'hue', label: 'Hue', min: 0, max: 360, unit: '\u00B0', bg: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' },
                { key: 'brightness', label: 'Brightness', min: 0, max: 200, unit: '%' },
                { key: 'contrast', label: 'Contrast', min: 0, max: 200, unit: '%' },
                { key: 'saturation', label: 'Saturation', min: 0, max: 200, unit: '%' },
                { key: 'opacity', label: 'Opacity', min: 0, max: 100, unit: '%', bg: 'linear-gradient(to right, transparent, #fff)' },
              ].map(s => (
                <div key={s.key}>
                  <label className="text-white text-xs">{s.label}: {adj[s.key]}{s.unit}</label>
                  <input
                    type="range" min={s.min} max={s.max}
                    value={adj[s.key]}
                    onChange={(e) => updateAdjustment(selItem.id, s.key, parseInt(e.target.value))}
                    className="w-full"
                    style={s.bg ? { background: s.bg } : {}}
                  />
                </div>
              ))}
              <button onClick={() => setShowColorPanel(false)} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 rounded-lg text-sm">
                Done
              </button>
            </div>
          </div>
        );
      })()}

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-[9999] animate-bounce">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-xl shadow-2xl border-2 border-white flex items-center gap-2">
            <span className="text-2xl">✅</span>
            <span className="font-bold text-sm">Outfit Saved!</span>
          </div>
        </div>
      )}
      {showErrorToast && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-[9999] animate-bounce">
          <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 py-2 rounded-xl shadow-2xl border-2 border-white flex items-center gap-2">
            <span className="text-2xl">❌</span>
            <span className="font-bold text-sm">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Modals */}
      {showTips && <TipsModal onClose={() => setShowTips(false)} />}
      {showNoteModal && <NoteModal onSave={(t) => { addNote(t); setShowNoteModal(false); }} onClose={() => setShowNoteModal(false)} />}
      {showSaveModal && <SaveOutfitModal onSave={saveOutfit} onClose={() => setShowSaveModal(false)} />}
      {showMyOutfits && (
        <MyOutfitsModal
          outfits={savedOutfits}
          onLoad={loadOutfit}
          onDelete={(id) => deleteOutfit(id)}
          onClose={() => setShowMyOutfits(false)}
        />
      )}
    </div>
  );
};

// ============================================================
// Avatar SVG Components — Split into Body and Head/Neck
// so head can render in front of or behind clothing items
// ============================================================

// ============================================================
// Draggable Clothing Item on Mannequin
// ============================================================
const DraggableClothingItem = ({ item, zone, zonePos, canvasRef, transform, adjustments, isSelected, hasSelection, onSelect, onTransformChange, zoomStep = 0.05, snapToGrid = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, tx: 0, ty: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [pinchStartDist, setPinchStartDist] = useState(0);
  const [pinchStartScale, setPinchStartScale] = useState(1);

  // Color adjustments + yellow glow when selected
  const filterStyle = `hue-rotate(${adjustments.hue}deg) brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)${isSelected ? ' drop-shadow(0 0 6px rgba(250,204,21,0.9)) drop-shadow(0 0 12px rgba(250,204,21,0.5))' : ''}`;

  // Position: zone dimensions, CSS transform handles scaling
  const getPosition = () => {
    if (!canvasRef.current) return { left: 0, top: 0, width: 80, height: 80 };
    const rect = canvasRef.current.getBoundingClientRect();
    const cw = rect.width;
    const ch = rect.height;

    const mw = Math.min(220, cw * 0.55);
    const mh = Math.min(440, ch * 0.92);
    const mx = (cw - mw) / 2;
    const my = (ch - mh) / 2;

    const zt = parseFloat(zonePos.top) / 100 * mh + my;
    const zl = parseFloat(zonePos.left) / 100 * mw + mx;
    const zw = parseFloat(zonePos.width) / 100 * mw;
    const zh = parseFloat(zonePos.height) / 100 * mh;

    const cx = zl + zw / 2 + transform.x;
    const cy = zt + zh / 2 + transform.y;

    return { left: cx - zw / 2, top: cy - zh / 2, width: zw, height: zh };
  };

  const pos = getPosition();

  const handlePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();

    if (e.touches && e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setPinchStartDist(Math.sqrt(dx * dx + dy * dy));
      setPinchStartScale(transform.scale);
      setIsPinching(true);
      return;
    }

    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStart({ x: cx, y: cy, tx: transform.x, ty: transform.y });
  };

  useEffect(() => {
    if (!isDragging && !isPinching) return;

    const handleMove = (e) => {
      if (isPinching && e.touches && e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const newScale = Math.max(0.05, Math.min(6, pinchStartScale * (dist / pinchStartDist)));
        onTransformChange({ scale: newScale });
        return;
      }
      if (isDragging) {
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        let newX = dragStart.tx + (cx - dragStart.x);
        let newY = dragStart.ty + (cy - dragStart.y);
        if (snapToGrid) {
          newX = Math.round(newX / 10) * 10;
          newY = Math.round(newY / 10) * 10;
        }
        onTransformChange({ x: newX, y: newY });
      }
    };

    const handleUp = () => { setIsDragging(false); setIsPinching(false); };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDragging, isPinching, dragStart, pinchStartDist, pinchStartScale]);

  const handleWheel = (e) => {
    if (!isSelected) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    onTransformChange({ scale: Math.max(0.05, Math.min(6, transform.scale + delta)) });
  };

  const isRealImage = item.image_url && (
    item.image_url.startsWith('http') || item.image_url.startsWith('/') || item.image_url.startsWith('data:')
  );
  const emoji = isRealImage ? null : (item.image_url || item.icon || '👕');

  return (
    <div
      className="absolute cursor-grab active:cursor-grabbing"
      style={{
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        width: `${pos.width}px`,
        height: `${pos.height}px`,
        transform: `scale(${transform.scale}) scaleX(${transform.flipX ? -1 : 1}) rotate(${transform.rotation || 0}deg)`,
        transformOrigin: 'center',
        zIndex: transform.zIndex || zonePos.zIndex,
        filter: filterStyle,
        opacity: (adjustments.opacity != null ? adjustments.opacity : 100) / 100,
        touchAction: 'none',
        pointerEvents: (hasSelection && !isSelected) ? 'none' : 'auto',
        transition: isDragging ? 'none' : 'left 0.1s, top 0.1s',
      }}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onWheel={handleWheel}
    >
      <div className="w-full h-full flex items-center justify-center">
        {isRealImage ? (
          <img
            src={item.image_url}
            alt={item.name_english}
            className="w-full h-full object-contain drop-shadow-lg pointer-events-none select-none"
            draggable={false}
          />
        ) : (
          <div className="drop-shadow-lg pointer-events-none select-none"
            style={{ fontSize: `${Math.max(28, pos.height * 0.6)}px` }}
          >
            <GameIcon icon={emoji} />
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================
// Draggable Note Component
// ============================================================
const DraggableNote = ({ note, isSelected, onSelect, onDelete, onUpdate }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleDown = (e) => {
    if (e.target.closest('.delete-btn')) return;
    e.preventDefault();
    e.stopPropagation();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStart({ x: cx - note.x, y: cy - note.y });
    onSelect();
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      onUpdate({ x: cx - dragStart.x, y: cy - dragStart.y });
    };
    const up = () => setIsDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [isDragging, dragStart]);

  return (
    <div
      className={`absolute bg-gradient-to-br from-yellow-400 to-orange-400 border-2 rounded-xl px-3 py-1.5 text-black font-bold text-xs sm:text-sm cursor-move shadow-lg max-w-[200px] ${
        isSelected ? 'ring-4 ring-blue-400' : ''
      }`}
      style={{ left: note.x, top: note.y, zIndex: note.zIndex, touchAction: 'none' }}
      onMouseDown={handleDown}
      onTouchStart={handleDown}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="delete-btn absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow"
      >
        x
      </button>
      <div className="pr-3">📝 {note.text}</div>
    </div>
  );
};

// ============================================================
// Note Modal
// ============================================================
const NoteModal = ({ onSave, onClose }) => {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-white mb-3">📝 Add Note</h3>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. 'Wedding outfit', 'Summer casual'"
          className="w-full p-3 rounded-lg resize-none min-h-[70px] mb-3 text-sm"
          autoFocus
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-white/20 hover:bg-white/30 text-white font-bold py-2 rounded-lg text-sm">Cancel</button>
          <button
            onClick={() => note.trim() && onSave(note)}
            disabled={!note.trim()}
            className={`flex-1 font-bold py-2 rounded-lg text-sm ${note.trim() ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Tips Modal
// ============================================================
const TipsModal = ({ onClose }) => {
  const [helpContent, setHelpContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dress-up/help`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setHelpContent(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">How to Play Dress Up</h3>
          <button onClick={onClose} className="text-white text-xl hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center">X</button>
        </div>
        <div className="space-y-3 text-white text-sm">
          {helpContent ? (
            <div className="bg-white/10 rounded-lg p-4 whitespace-pre-line">{helpContent.content}</div>
          ) : loading ? (
            <div className="text-center py-8 animate-pulse">Loading...</div>
          ) : (
            <>
              <div className="bg-white/10 rounded-lg p-3">
                <h4 className="font-bold mb-1">👕 Equip Items</h4>
                <p>Browse categories on the left, tap any item on the right to snap it onto the mannequin. Items go to the correct body zone automatically.</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <h4 className="font-bold mb-1">🔄 Swap & Remove</h4>
                <p>Tap an equipped item again to remove it. Tap a different item in the same category to replace it. Accessories can stack (glasses + earrings).</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <h4 className="font-bold mb-1">🎨 Change Colors</h4>
                <p>Click an equipped item on the mannequin, then tap the color button to adjust hue, brightness, contrast, and saturation.</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <h4 className="font-bold mb-1">📝 Notes & 💾 Save</h4>
                <p>Add sticky notes to remember outfit ideas. Save complete outfits to load them later.</p>
              </div>
              <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-3">
                <h4 className="font-bold mb-1">💡 Tip</h4>
                <p>Buy more clothes from the Souk to unlock them here! Every item you own appears in the sidebar.</p>
              </div>
            </>
          )}
        </div>
        <button onClick={onClose} className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white font-bold py-2 rounded-lg">Got it!</button>
      </div>
    </div>
  );
};

// ============================================================
// Save Outfit Modal
// ============================================================
const SaveOutfitModal = ({ onSave, onClose }) => {
  const [name, setName] = useState('');
  const [occasion, setOccasion] = useState('');
  const occasions = [
    { value: 'casual', label: '🌤 Casual / Yawmi' },
    { value: 'work', label: '💼 Work / Khdma' },
    { value: 'wedding', label: '💒 Wedding / 3rs' },
    { value: 'eid', label: '🌙 Eid / L3id' },
    { value: 'party', label: '🎉 Party / Hafla' },
    { value: 'beach', label: '🏖 Beach / Lbhar' },
    { value: 'traditional', label: '🕌 Traditional / Taqlidi' }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-white mb-3">💾 Save Outfit</h3>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Name your outfit" className="w-full p-3 rounded-lg mb-3" autoFocus
        />
        <select value={occasion} onChange={(e) => setOccasion(e.target.value)} className="w-full p-3 rounded-lg mb-3">
          <option value="">Select Occasion</option>
          {occasions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-white/20 hover:bg-white/30 text-white font-bold py-2 rounded-lg text-sm">Cancel</button>
          <button
            onClick={() => name && onSave(name, occasion)}
            disabled={!name}
            className={`flex-1 font-bold py-2 rounded-lg text-sm ${name ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// My Outfits Modal
// ============================================================
const MyOutfitsModal = ({ outfits, onLoad, onDelete, onClose }) => {
  const [occasionFilter, setOccasionFilter] = useState('');
  const filtered = occasionFilter ? outfits.filter(o => o.occasion === occasionFilter) : outfits;
  const occasions = [...new Set(outfits.map(o => o.occasion).filter(Boolean))];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-5 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xl font-bold text-white">📂 My Saved Outfits</h3>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg font-bold text-sm">X</button>
        </div>
        {occasions.length > 0 && (
          <div className="flex gap-1 mb-3 flex-wrap">
            <button
              onClick={() => setOccasionFilter('')}
              className={`px-2 py-1 rounded-full text-xs font-bold ${!occasionFilter ? 'bg-white text-purple-600' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              All ({outfits.length})
            </button>
            {occasions.map(occ => (
              <button
                key={occ}
                onClick={() => setOccasionFilter(occ)}
                className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${occasionFilter === occ ? 'bg-white text-purple-600' : 'bg-white/20 text-white hover:bg-white/30'}`}
              >
                {occ} ({outfits.filter(o => o.occasion === occ).length})
              </button>
            ))}
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="text-white/60 text-center py-10 text-sm">
            {outfits.length === 0 ? 'No saved outfits yet! Dress up and save your first outfit.' : 'No outfits match this filter.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(outfit => (
              <div key={outfit.created_at || outfit.id} className="bg-white/10 rounded-lg p-3">
                <div className="text-white font-bold text-sm mb-1">{outfit.name}</div>
                {outfit.occasion && <div className="text-yellow-400 text-xs mb-2 capitalize">{outfit.occasion}</div>}
                <div className="flex gap-2">
                  <button onClick={() => onLoad(outfit)} className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-1.5 rounded">Load</button>
                  <button onClick={() => onDelete(outfit.created_at || outfit.id)} className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-2 py-1.5 rounded">🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DressUpAvatar;
